import React from "react";
import PropTypes from "prop-types";
import useCanDo from "src/hooks/useCanDo";
import NoPermissionView from "src/views/errors/NoPermissionView";
import { normalizeToNewActions } from "src/utils/employeeActionAliases";

// F-role-gates · 2026-08-25 · Guard granular por acción.
//
// Diseño:
//   • Envuelve components de rutas administrativas (o secciones dentro
//     de una vista) para bloquear acceso por URL directa aunque el
//     item del NavBar esté oculto.
//   • Usa `useCanDo` — que aplica la regla "admin dueño (sin sesión de
//     empleado activa) pasa todo". Sólo cuando hay `employeeSession.info`
//     valida contra `info.actions[]`.
//   • Aditivo: NO reemplaza al `RoleBasedGuard` legacy. Se aplica sobre
//     el `component:` del route config, dejando el guard general para el
//     chequeo de rol global (USER_NORMAL vs USER_FRANQUICIA, etc.).
//   • Sin dependencia de `user.requestUserIdentity` — si el owner no ha
//     activado esa opción, el guard sigue funcionando porque `useCanDo`
//     hace pass-through cuando no hay sesión de empleado firmada.
//
// Props:
//   • `action`  string | string[] — acción(es) del EmployeeAction enum
//                 (o alias legacy ROLES_ENUM). Se expanden con
//                 withLegacyAliases para aceptar los nombres viejos.
//   • `mode`    "any" | "all" — cuando `action` es array, si basta con
//                 tener una (default "any") o hay que tener todas.
//   • `sectionLabel` string opcional — nombre humano de la sección para
//                 el fallback (ej: "Empleados", "Facturación").
//   • `fallback` opcional — reemplaza el NoPermissionView por defecto.
//
// Uso mínimo:
//   <RequireAction action="EMPLOYEE_MANAGE" sectionLabel="Empleados">
//     <EmployeesList/>
//   </RequireAction>
//
// Cualquier alias legacy sirve también:
//   <RequireAction action="RESTAURANT_EMPLOYEE_MANAGER">...
//
// Registro centralizado — en vez de repetir la acción en cada route,
// usar el HOC `withRequireAction(Component, "ACTION")` desde el
// route config para envolver el lazy import.

// La expansión legacy→nuevas se hace en `normalizeToNewActions`
// (util compartido con `<Can>`) — `useCanDo` chequea contra
// `info.actions[]` que sólo trae codenames nuevos firmados por el
// backend, así que ningún string legacy matchearía sin este paso.

RequireAction.propTypes = {
  action: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
  mode: PropTypes.oneOf(["any", "all"]),
  sectionLabel: PropTypes.string,
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default function RequireAction({
  action,
  mode = "any",
  sectionLabel,
  fallback,
  children,
}) {
  const { can, hasSession } = useCanDo();

  // Admin dueño (sin sesión de empleado firmada) → pasa siempre.
  // Coherente con la regla del backend `AllowRestaurantOwner=true`.
  if (!hasSession) return children;

  const set = normalizeToNewActions(action);
  if (set.length === 0) return children; // config incorrecta, no bloqueamos

  const ok = mode === "all" ? set.every((a) => can(a)) : set.some((a) => can(a));

  if (ok) return children;

  return fallback ?? (
    <NoPermissionView action={Array.isArray(action) ? action : [action]} sectionLabel={sectionLabel} />
  );
}

// HOC helper — envuelve un lazy component sin tener que hacerlo inline
// en cada `component:` del route config.
//
//   const Employees = lazy(() => import("src/views/.../employees"));
//   const GuardedEmployees = withRequireAction(Employees, "EMPLOYEE_MANAGE", "Empleados");
export function withRequireAction(Component, action, sectionLabel) {
  const Wrapped = (props) => (
    <RequireAction action={action} sectionLabel={sectionLabel}>
      <Component {...props} />
    </RequireAction>
  );
  Wrapped.displayName = `RequireAction(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
}
